import React, { FC, useEffect, useState } from "react"
import { SortParams, SummaryTrackStream } from "../../../../types"
import { KSpan, KTooltip } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { formatNumber } from "../../../../utility"

type SummaryTrackStreamsProps = {
  summaryTrackStreams: SummaryTrackStream[]
  handleSort: (field: string) => void
  sort: SortParams
}

const SummaryTrackStreams: FC<SummaryTrackStreamsProps> = (props) => {
  const { summaryTrackStreams, handleSort, sort } = props

  const renderSummaryTrackStreams = () => {
    const sortedSummaryTrackStreams = summaryTrackStreams?.sort((a, b) => {
      const currentA = a.stream || 0
      const changeA = a.streamChange || 0
      const previousA = currentA - changeA
      const percentageA = previousA !== 0 ? (changeA / previousA) * 100 : 0

      const currentB = b.stream || 0
      const changeB = b.streamChange || 0
      const previousB = currentB - changeB
      const percentageB = previousB !== 0 ? (changeB / previousB) * 100 : 0

      if (sort.field && sort.direction) {
        if (sort.direction === "asc") {
          return currentA - currentB
        } else {
          return currentB - currentA
        }
      } else {
        return Math.abs(percentageB) - Math.abs(percentageA)
      }
    })

    return sortedSummaryTrackStreams?.map((summaryTrackStream) => {
      const background = "#00000008"
      const isPositiveStream = summaryTrackStream.streamChange > 0
      return (
        <div
          className="flex p-2"
          style={{ width: "100%", background, borderRadius: "5px", borderBottom: "5px solid white" }}
        >
          <div style={{ width: "24%" }}>
            <div>
              <KTooltip
                position="bottom"
                content={<KSpan text={summaryTrackStream?.user?.username || "-"} />}
                children={
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FRONT_END_URL}/admin/user-asset?user_id=${summaryTrackStream?.userId}`,
                        "_blank"
                      )
                    }
                  >
                    <KSpan
                      text={`${summaryTrackStream?.user?.username?.substring(0, 14) || "-"}${
                        (summaryTrackStream?.user?.username?.length || 0) > 14 ? "..." : ""
                      }`}
                    />
                  </div>
                }
              />
              <div
                className="cursor-pointer"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_FRONT_END_URL}/admin/catalog?isrc=${summaryTrackStream?.isrc}`,
                    "_blank"
                  )
                }
              >
                <KSpan text={summaryTrackStream?.isrc || "-"} />
              </div>
            </div>
          </div>
          <div style={{ width: "30%" }}>
            <KSpan text={summaryTrackStream.track?.title || "-"} />
          </div>
          <div style={{ width: "30%" }}>
            <KSpan text={summaryTrackStream.track?.artistList?.map((artist) => artist.name)?.join(", ") || "-"} />
          </div>
          <div style={{ width: "16%" }}>
            <div className="flex flex-col gap-1">
              <KSpan text={formatNumber(summaryTrackStream.stream || 0, 0)} />
              <div className="flex gap-1">
                <KSpan
                  text={`${isPositiveStream ? "+" : ""}${formatNumber(summaryTrackStream.streamChange || 0, 1)}`}
                  color={isPositiveStream ? "#56E099" : "#FF5865"}
                />
                <KSpan
                  text={`(${(() => {
                    const current = summaryTrackStream.stream || 0
                    const change = summaryTrackStream.streamChange || 0
                    const previous = current - change
                    return previous !== 0 ? (change / previous) * 100 : 0
                  })().toFixed(0)}%)`}
                  color={isPositiveStream ? "#56E099" : "#FF5865"}
                />
              </div>
            </div>
          </div>
        </div>
      )
    })
  }

  return (
    <div className="w-full h-full">
      <div
        className="py-5 px-7 min-h-[100%]"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex mb-4" style={{ width: "100%" }}>
            <div style={{ width: "24%" }}>
              <KSpan color="#111" text={"User / Identifier"} />
            </div>
            <div style={{ width: "30%" }}>
              <KSpan color="#111" text={"Track Title"} />
            </div>
            <div style={{ width: "30%" }}>
              <KSpan color="#111" text={"Artist(s)"} />
            </div>
            <div
              className="cursor-pointer flex gap-1 items-center"
              style={{ width: "16%" }}
              onClick={() => handleSort("sum_stream")}
            >
              <KSpan color="#111" text={"Streams"} />
              {sort.field && sort.field === "sum_stream" && (
                <img
                  src={sort.direction === "asc" ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                  alt="change-icon"
                  height={16}
                  width={16}
                />
              )}
            </div>
          </div>
          {summaryTrackStreams && !(summaryTrackStreams.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          {summaryTrackStreams && summaryTrackStreams.length > 0 && (
            <div className="flex p-2 mb-2" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
              <div style={{ width: "84%" }}>
                <KSpan fontWeight={600} color="#111" text={"TOTAL"} />
              </div>
              <div style={{ width: "16%" }}>
                <div className="flex flex-col gap-1">
                  <KSpan text={formatNumber(summaryTrackStreams.reduce((sum, item) => sum + (item.stream || 0), 0))} />
                  <KSpan
                    text={formatNumber(summaryTrackStreams.reduce((sum, item) => sum + (item.streamChange || 0), 0))}
                  />
                </div>
              </div>
            </div>
          )}
          {renderSummaryTrackStreams()}
        </div>
      </div>
    </div>
  )
}

export default SummaryTrackStreams
